/**
 * 统一导航状态管理 Hook
 * 管理所有区域的选中、编辑和悬停状态
 */

import { useState, useCallback } from 'react';

/**
 * 统一位置标识
 */
export type UnifiedPosition =
  | { type: 'scenesTable'; sceneID: string; row: number; col: number; colKey?: string }
  | { type: 'sceneDetails'; sceneID: string; field: string }
  | { type: 'elementTable'; elementID: string; field: 'name' | 'quantity' };

/**
 * 当前状态和位置（选中或编辑）
 */
export type CurrentStateAndPosition =
  | { state: 'selected'; type: 'scenesTable'; sceneID: string; row: number; col: number; colKey?: string }
  | { state: 'selected'; type: 'sceneDetails'; sceneID: string; field: string }
  | { state: 'selected'; type: 'elementTable'; elementID: string; field: 'name' | 'quantity' }
  | { state: 'editing'; type: 'scenesTable'; sceneID: string; row: number; col: number; colKey?: string }
  | { state: 'editing'; type: 'sceneDetails'; sceneID: string; field: string }
  | { state: 'editing'; type: 'elementTable'; elementID: string; field: 'name' | 'quantity' }
  | null;

/**
 * 统一状态
 */
export type UnifiedState = 'normal' | 'hover' | 'selected' | 'editing';

/**
 * 位置比较函数
 */
export function positionsEqual(a: UnifiedPosition | null, b: UnifiedPosition | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.type !== b.type) return false;
  
  switch (a.type) {
    case 'scenesTable':
      // 排序会改变 row 索引，但 sceneID 唯一标识场次，故仅匹配 sceneID 和 col
      // 这样切换排序后选中行仍能正确高亮（定位到场号 43 的行而非第 60 行）
      return b.type === 'scenesTable' && 
        a.sceneID === b.sceneID && 
        a.col === b.col;
    case 'sceneDetails':
      return b.type === 'sceneDetails' && 
        a.sceneID === b.sceneID && 
        a.field === b.field;
    case 'elementTable':
      return b.type === 'elementTable' && 
        a.elementID === b.elementID && 
        a.field === b.field;
    default:
      return false;
  }
}

/**
 * 从 CurrentStateAndPosition 提取 UnifiedPosition
 */
function extractPosition(stateAndPosition: CurrentStateAndPosition): UnifiedPosition | null {
  if (!stateAndPosition) return null;
  
  const { state, ...position } = stateAndPosition;
  return position as UnifiedPosition;
}

/**
 * 统一导航状态管理 Hook
 */
export function useUnifiedNavigationState() {
  const [currentStateAndPosition, setCurrentStateAndPosition] = useState<CurrentStateAndPosition>(null);
  const [hoverPosition, setHoverPosition] = useState<UnifiedPosition | null>(null);
  

  /**
   * 获取位置状态
   * 优先级：editing > selected > hover > normal
   */
  const getPositionState = useCallback((position: UnifiedPosition): UnifiedState => {
    // 检查当前状态和位置
    if (currentStateAndPosition) {
      const currentPosition = extractPosition(currentStateAndPosition);
      if (currentPosition) {
        const isEqual = positionsEqual(currentPosition, position);
        if (isEqual) {
          return currentStateAndPosition.state;
        }
        // 注意：位置不匹配是正常的，因为渲染时每个单元格都会调用 getPositionState
        // 只有当前选中的单元格会匹配，其他单元格不匹配是预期行为
        // 不再记录位置不匹配的日志，避免控制台刷屏
      }
    }

    // 悬停状态
    if (hoverPosition && positionsEqual(hoverPosition, position)) {
      return 'hover';
    }

    return 'normal';
  }, [currentStateAndPosition, hoverPosition]);

  /**
   * 获取当前状态和位置
   */
  const getCurrentState = useCallback((): CurrentStateAndPosition => {
    return currentStateAndPosition;
  }, [currentStateAndPosition]);

  /**
   * 设置选中位置
   * 自动清除之前的编辑或选中状态（互斥）
   */
  const setSelection = useCallback((position: UnifiedPosition | null) => {
    if (position === null) {
      setCurrentStateAndPosition(null);
    } else {
      const newState: CurrentStateAndPosition = {
        state: 'selected',
        ...position,
      } as CurrentStateAndPosition;
      
      setCurrentStateAndPosition(newState);
    }
  }, []);

  /**
   * 设置编辑位置
   * 自动清除之前的编辑或选中状态（互斥）
   */
  const setEditing = useCallback((position: UnifiedPosition | null) => {
    if (position === null) {
      setCurrentStateAndPosition(null);
    } else {
      setCurrentStateAndPosition({
        state: 'editing',
        ...position,
      } as CurrentStateAndPosition);
    }
  }, []);

  /**
   * 设置悬停位置
   */
  const setHover = useCallback((position: UnifiedPosition | null) => {
    setHoverPosition(position);
  }, []);

  /**
   * 清除所有状态
   */
  const clearAllStates = useCallback(() => {
    setCurrentStateAndPosition(null);
    setHoverPosition(null);
  }, []);

  // 为了向后兼容，提供 selectionPosition 和 editingPosition 的计算属性
  const selectionPosition = currentStateAndPosition?.state === 'selected' 
    ? extractPosition(currentStateAndPosition) 
    : null;
  const editingPosition = currentStateAndPosition?.state === 'editing' 
    ? extractPosition(currentStateAndPosition) 
    : null;

  return {
    currentStateAndPosition,
    selectionPosition, // 向后兼容
    editingPosition, // 向后兼容
    hoverPosition,
    getPositionState,
    getCurrentState,
    setSelection,
    setEditing,
    setHover,
    clearAllStates,
  };
}
